import React, { useState } from 'react';
import { useTranslation } from '../i18n';
import { formatMoney } from '../utils/format';
import { Wallet, CreditCard, TrendingUp, TrendingDown, PiggyBank, BarChart3, FileText, Briefcase, Layers, Globe2, Home } from 'lucide-react';
import useFinanceData from '../components/finance/useFinanceData';
import OverviewTab from '../components/finance/OverviewTab';
import PnlTab from '../components/finance/PnlTab';
import CreditsTab from '../components/finance/CreditsTab';
import AmortissementsTab from '../components/finance/AmortissementsTab';
import ExportCostsTab from '../components/finance/ExportCostsTab';
import LeaseAidsTab from '../components/finance/LeaseAidsTab';
import HistoryTab from '../components/finance/HistoryTab';

/**
 * Finance — page orchestrator. Tab navigation + summary KPI header.
 * The heavy per-tab content lives in dedicated components under
 * `components/finance/` so each piece stays under ~200 LOC and can be
 * unit-tested individually. Refactored 28/05/2026 from a 666-line monolith.
 */
export default function Finance() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('overview');
  const { treasury, monthlyRevenues, totalMonthlyCharges, netResult } = useFinanceData();

  return (
    <div className="space-y-4" data-testid="finance-page">
      <h2 className="text-2xl font-bold text-foreground">{t('finance_title')}</h2>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-4 h-4 text-foreground" />
            <span className="text-xs text-slate-500">{t('treasury')}</span>
          </div>
          <p className={`text-xl font-bold ${treasury >= 0 ? 'text-foreground' : 'text-danger'}`}>
            {formatMoney(treasury)}
          </p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-money" />
            <span className="text-xs text-slate-500">{t('revenue_per_month')}</span>
          </div>
          <p className="text-xl font-bold text-money">{formatMoney(monthlyRevenues)}</p>
        </div>
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-danger" />
            <span className="text-xs text-slate-500">{t('charges_per_month')}</span>
          </div>
          <p className="text-xl font-bold text-danger">{formatMoney(totalMonthlyCharges)}</p>
        </div>
        <div className={`bg-ink-800 rounded-lg border p-4 ${netResult >= 0 ? 'border-money/40' : 'border-danger/40'}`}>
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank className={`w-4 h-4 ${netResult >= 0 ? 'text-money' : 'text-danger'}`} />
            <span className="text-xs text-slate-500">{t('net_result')}</span>
          </div>
          <p className={`text-xl font-bold ${netResult >= 0 ? 'text-money' : 'text-danger'}`}>
            {netResult >= 0 ? '+' : ''}{formatMoney(netResult)}
          </p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { id: 'overview', label: t('overview'), icon: BarChart3 },
          { id: 'pnl', label: t('income_statement'), icon: FileText },
          { id: 'credits', label: t('credits_tab'), icon: CreditCard },
          { id: 'amortissements', label: 'Amortissements', icon: Layers },
          { id: 'lease', label: 'Loyers & Aides', icon: Home },
          { id: 'export', label: 'Coûts export', icon: Globe2 },
          { id: 'history', label: 'Historique annuel', icon: Briefcase },
        ].map(tab_item => (
          <button
            key={tab_item.id}
            onClick={() => setTab(tab_item.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              tab === tab_item.id ? 'bg-accent text-white' : 'bg-ink-800 text-slate-400 border border-ink-600'
            }`}
            data-testid={`finance-tab-${tab_item.id}`}
          >
            <tab_item.icon className="w-3.5 h-3.5" /> {tab_item.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && <OverviewTab />}
      {tab === 'pnl' && <PnlTab />}
      {tab === 'credits' && <CreditsTab />}
      {tab === 'amortissements' && <AmortissementsTab />}
      {tab === 'lease' && <LeaseAidsTab />}
      {tab === 'export' && <ExportCostsTab />}
      {tab === 'history' && <HistoryTab />}
    </div>
  );
}
